import * as React from "react";

type UseFilterProps<T> = {
  service: any;
  initialData: T[];
  student_teacher: string;
  clientQuery?: Record<string, unknown>;
  /** Usado para búsquedas con query param directo (ej. `q`). */
  queryKey?: string;
};

function usesClientSearchTerm(clientQuery?: Record<string, unknown>) {
  return (
    clientQuery?.studentWithoutPrograms != null ||
    clientQuery?.teacherWithoutPrograms != null
  );
}

export default function useFilter<T>({
  service,
  initialData,
  student_teacher,
  clientQuery,
  queryKey = "q",
}: UseFilterProps<T>) {
  const [data, setData] = React.useState<T[]>(initialData);
  const [query, setQuery] = React.useState<string>("");
  const [filteredData, setFilteredData] = React.useState<T[]>(initialData);
  const [loading, setLoading] = React.useState<boolean>(false);
  const clientScopeId =
    clientQuery?.studentWithoutPrograms ?? clientQuery?.teacherWithoutPrograms;

  React.useEffect(() => {
    setFilteredData(initialData);
  }, [initialData]);

  React.useEffect(() => {
    const fetchData = async () => {
      if (!query) return;
      try {
        setLoading(true);

        const findQuery = usesClientSearchTerm(clientQuery)
          ? {
              skipJoin: true,
              student_teacher,
              $client: {
                ...(clientQuery ?? {}),
                searchTerm: query,
              },
            }
          : {
              skipJoin: true,
              student_teacher,
              [queryKey]: query,
              ...(clientQuery ? { $client: clientQuery } : {}),
            };

        const response = await service.find({ query: findQuery });
        setData(response?.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query, student_teacher, clientScopeId, queryKey, service, clientQuery]);

  React.useEffect(() => {
    setFilteredData(query ? data : initialData);
  }, [data, query, initialData]);

  return {
    query,
    setQuery,
    data,
    filteredData,
    loading,
  };
}
